import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { TCart, TUpdateCart } from '@/types/carts';
import { cartServices } from '@/app/[locale]/carts/services/cart.service';

interface CartState {
  carts: TCart[];
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  forceUpdate: () => void;
  fetchCart: () => Promise<void>;
  addToCart: (item: TUpdateCart) => Promise<void>;
  removeFromCart: (cartId: number | string, itemId: string) => Promise<void>;
  deleteCart: (cartId: number | string) => Promise<void>;
  updateCart: (item: TUpdateCart) => Promise<void>;
  totalItems: number;
  calculateTotalItems: (carts: TCart[]) => number;
  getCartIdByProductId: (productId: string) => number | null;
  setCarts: (carts: TCart[]) => void;
  deleteCartFromUI: (cartId: number | string) => void;
}

const useCartStore = create<CartState>()(
  devtools(
    persist(
      (set, get) => ({
        carts: [],
        loading: false,
        error: null,
        totalItems: 0,

        forceUpdate: () => set((state) => ({ ...state })), // Add forceUpdate function

        setError: (error: string | null) => {
          set({ error });
        },

        setCarts: (carts: TCart[]) => {
          set({ carts, totalItems: get().calculateTotalItems(carts) });
        },

        fetchCart: async () => {
          set({ loading: true, error: null });
          try {
            const data = await cartServices.getAllCarts();
            const totalItems = get().calculateTotalItems(data);
            set({ carts: data, totalItems, loading: false });
          } catch (error: any) {
            set({ error: error.message, loading: false });
          }
        },

        addToCart: async (item: TUpdateCart) => {
          set((state) => ({ ...state, loading: true, error: null }));
          try {
            const response = await cartServices.addCart(item);

            if (response) {
              const existingCartIndex = get().carts.findIndex((cart) => cart.id === response.id);
              let updatedCart;

              if (existingCartIndex !== -1) {
                updatedCart = get().carts.map((cart, index) =>
                  index === existingCartIndex ? response : cart,
                );
              } else {
                updatedCart = [...get().carts, response];
              }

              const totalItems = get().calculateTotalItems(updatedCart);

              set({
                carts: updatedCart,
                loading: false,
                totalItems: totalItems,
                error: null,
              });
              get().forceUpdate();
            } else {
              set((state) => ({ ...state, error: 'Error adding to cart', loading: false }));
              get().forceUpdate();
            }
          } catch (error: any) {
            set((state) => ({ ...state, error: error.message, loading: false }));
          }
        },

        updateCart: async (item: TUpdateCart) => {
          set((state) => ({ ...state, loading: true, error: null }));

          try {
            const response = await cartServices.updateCart(item);

            if (response) {
              const existingCartIndex = get().carts.findIndex((cart) => cart.id === response.id);

              let updatedCarts;

              if (existingCartIndex !== -1) {
                // Update the existing cart
                updatedCarts = get().carts.map((cart, index) =>
                  index === existingCartIndex ? response : cart,
                );
              } else {
                // Add the new cart
                updatedCarts = [...get().carts, response];
              }

              const totalItems = get().calculateTotalItems(updatedCarts);

              set({
                carts: updatedCarts,
                loading: false,
                totalItems,
                error: null,
              });
              get().forceUpdate();
            }
          } catch (error: any) {
            set((state) => ({ ...state, error: error.message, loading: false }));
            get().forceUpdate();
          }
        },

        removeFromCart: async (cartId: string | number, itemId: string) => {
          set({ loading: true, error: null });
          try {
            const response = await cartServices.removeItem(cartId, itemId);
            if (response) {
              let updatedCarts;
              if (response.cartItems.length === 0) {
                // Remove the cart if cartItems is empty
                updatedCarts = get().carts.filter((cart) => cart.id !== response.id);
              } else {
                const existingCartIndex = get().carts.findIndex((cart) => cart.id === response.id);
                if (existingCartIndex !== -1) {
                  updatedCarts = get().carts.map((cart, index) =>
                    index === existingCartIndex ? response : cart,
                  );
                } else {
                  updatedCarts = [...get().carts, response];
                }
              }
              const totalItems = get().calculateTotalItems(updatedCarts);
              set({
                carts: updatedCarts,
                loading: false,
                totalItems,
              });
              get().forceUpdate();
            }
          } catch (error: any) {
            set({ error: error.message, loading: false });
          }
        },

        deleteCart: async (cartId: string | number) => {
          set({ loading: true, error: null });
          try {
            const respose = await cartServices.removeCart(cartId);
            if (respose) {
              const updatedCarts = get().carts.filter((cart) => cart.id !== cartId);
              const totalItems = get().calculateTotalItems(updatedCarts);
              set({ carts: updatedCarts, loading: false, totalItems });
              get().forceUpdate();
            }
          } catch (error: any) {
            set({ error: error.message, loading: false });
          }
        },

        deleteCartFromUI: (cartId: string | number) => {
          set({ loading: true, error: null });

          // Filter out the cart by ID
          const updatedCarts = get().carts.filter((cart) => Number(cart.id) !== Number(cartId));

          // Recalculate total items after deletion
          const totalItems = get().calculateTotalItems(updatedCarts);

          // Update the state once with the new values
          set((state) => ({
            ...state,
            carts: updatedCarts,
            loading: false,
            totalItems,
          }));

          get().forceUpdate();
        },

        calculateTotalItems: (carts: TCart[]) => {
          return carts.reduce(
            (total, cart) => total + cart.cartItems.reduce((sum, item) => sum + item.quantity, 0),
            0,
          );
        },

        getCartIdByProductId: (productId: string) => {
          const cart = get().carts.find((cart) =>
            cart.cartItems.some((item) => item.itemId === productId),
          );
          return cart ? cart.id : null;
        },
      }),
      { name: 'cart-storage' },
    ),
  ),
);

export default useCartStore;
